import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { reactSelectReset } from '../utils/utils';

interface BoardCertificationOption {
  value: string;
  description: string;
}

interface BoardCertification {
  value: string;
  boardEligible: boolean;
}

interface BoardCertificationComboSelectProps {
  options: BoardCertificationOption[];
  onChange: (certifications: BoardCertification[]) => void;
  className?: string;
  initialValue?: BoardCertification[];
  disabled?: boolean;
}

export const BoardCertificationComboSelect: React.FC<BoardCertificationComboSelectProps> = ({
  options,
  onChange,
  className = '',
  initialValue = [],
  disabled = false,
}) => {
  const [certifications, setCertifications] = useState<BoardCertification[]>(initialValue);
  const [selectedValue, setSelectedValue] = useState<any>(null);

  useEffect(() => {
    setCertifications(initialValue);
  }, [initialValue]);

  const selectOptions = options
    .filter(
      (opt) => opt.value !== 'NONE' && !certifications.some((cert) => cert.value === opt.value)
    )
    .map((option) => ({
      value: option.value,
      label: option.description,
    }));

  const handleAddCertification = (selectedOption: any) => {
    if (disabled) return;

    const newCertification: BoardCertification = {
      value: selectedOption.value,
      boardEligible: true,
    };
    const updatedCertifications = [...certifications, newCertification];
    setCertifications(updatedCertifications);
    onChange(updatedCertifications);
    setSelectedValue(null);
  };

  const handleRemoveCertification = (value: string) => {
    if (disabled) return;

    const updatedCertifications = certifications.filter((cert) => cert.value !== value);
    setCertifications(updatedCertifications);
    onChange(updatedCertifications);
  };

  const handleToggleEligible = (value: string) => {
    if (disabled) return;

    const updatedCertifications = certifications.map((cert) =>
      cert.value === value ? { ...cert, boardEligible: !cert.boardEligible } : cert
    );
    setCertifications(updatedCertifications);
    onChange(updatedCertifications);
  };

  return (
    <div className={className}>
      <Select
        options={selectOptions}
        value={selectedValue}
        onChange={handleAddCertification}
        placeholder="Start typing or select from list..."
        isClearable={false}
        isSearchable={true}
        styles={reactSelectReset}
        className="tw:mb-2"
        isDisabled={disabled}
      />
      <div className="tw:space-y-2 tw:mt-2">
        {certifications.map((cert) => (
          <div
            key={`board-cert-${cert.value}`}
            className="tw:flex tw:items-center tw:gap-2 tw:p-2 tw:bg-brand-green-0/5 tw:border tw:border-brand-green-0 tw:rounded-lg tw:dark:border-brand-green-1 tw:dark:bg-white/5"
          >
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemoveCertification(cert.value)}
                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
              >
                <i className="fat fa-xmark fa-md tw:h-4 tw:w-4 tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90" />
              </button>
            )}
            <span className="tw:flex-1 tw:text-brand-green-0 tw:dark:text-brand-green-1">
              {options.find((opt) => opt.value === cert.value)?.description || cert.value}
            </span>
            <label className="tw:!flex tw:items-center tw:gap-2">
              <span className="tw:text-sm tw:dark:text-brand-green-1">Board Eligible?</span>
              <input
                type="checkbox"
                checked={cert.boardEligible}
                onChange={() => handleToggleEligible(cert.value)}
                disabled={disabled}
                className="tw:flex-shrink-0 tw:w-5 tw:h-5 tw:ml-4 tw:appearance-none tw:rounded tw:border-2 tw:border-zinc-300 tw:checked:border-brand-green-1 tw:checked:bg-white tw:relative tw:before:content-[''] tw:before:block tw:before:w-3 tw:before:h-3 tw:before:absolute tw:before:top-1/2 tw:before:left-1/2 tw:before:-translate-x-1/2 tw:before:-translate-y-1/2 tw:checked:before:bg-brand-green-1 tw:transition-all tw:dark:border-white/10 tw:dark:checked:bg-transparent tw:dark:checked:border-brand-green-1 tw:disabled:opacity-90 tw:disabled:cursor-not-allowed"
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};
